import { DialogRef } from '@angular/cdk/dialog';
import {
  Component,
  computed,
  DestroyRef,
  forwardRef,
  inject,
  signal,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { SupportTicketsService, ToastService } from '@services';
import { FileInputComponent } from '@ui-lib';
import { catchError, throwError } from 'rxjs';

@Component({
  selector: 'icaredx-support-ticket-dialog',
  imports: [
    forwardRef(() => FileInputComponent),
    FormsModule,
    ReactiveFormsModule,
  ],
  templateUrl: './support-ticket-dialog.component.html',
})
export class SupportTicketDialogComponent {
  private readonly formBuilder = inject(FormBuilder);
  private readonly dialogRef = inject<DialogRef<string>>(DialogRef<string>);
  private readonly destroyRef = inject(DestroyRef);

  private readonly supportTicketsService = inject(SupportTicketsService);
  private readonly toastService = inject(ToastService);

  @ViewChild(FileInputComponent, { static: true })
  fileInputComponent!: FileInputComponent;

  isLoading = signal(false);

  fileLoading = computed(() => this.fileInputComponent.fileLoading());
  fileData = computed(() => this.fileInputComponent.fileData());
  imgSrc = computed(() =>
    this.fileData() ? URL.createObjectURL(this.fileData()!) : null
  );

  errorMessage = signal<string>('');

  form = this.formBuilder.nonNullable.group({
    subject: ['', [Validators.required]],
    message: ['', [Validators.required, Validators.maxLength(2000)]],
  });

  submit(): void {
    if (!this.form.valid) return;

    const { subject, message } = this.form.getRawValue();
    const formData = new FormData();
    formData.append('subject', subject);
    formData.append('message', message);
    if (this.fileData()) {
      formData.append('attachment', this.fileData()!);
    }

    this.isLoading.set(true);

    this.supportTicketsService
      .createTicket(formData)
      .pipe(
        catchError(error => {
          this.isLoading.set(false);
          this.errorMessage.set(
            error?.error?.message ||
              'An error occurred while submitting the support ticket.'
          );
          return throwError(() => error);
        })
      )
      .subscribe(() => {
        this.isLoading.set(false);
        this.errorMessage.set('');
        this.toastService.showSuccess('Your message was sent.');
        this.dialogRef.close();
      });
  }

  discard(): void {
    this.dialogRef.close();
  }

  openFilePicker(): void {
    if (this.fileInputComponent) {
      this.fileInputComponent.openFilePicker();
    }
  }

  removeFile(): void {
    this.fileInputComponent.fileData.set(null);
  }
}
